/**
 * Operation State Capture Utilities
 * Extracted from useNodeOperations.ts to eliminate duplication
 * Captures before-state for undo/redo functionality
 */

import type { ProjectData, ProjectNode, NodeOperation } from '@/shared/types/projectData';
import { normalizeKanbanColumnId } from '@/shared/utils/kanbanState';
import { getNodeId } from '@/features/projects/utils/projectUtils';
import { classifyGanttGroupMoveDescendants } from '@/shared/utils/ganttGroupMovePolicy';

function getSiblingNodeIds(projectData: ProjectData, parentId: string | null): string[] {
  return projectData.nodes
    .filter((node) => (node.parentId || null) === parentId)
    .sort((left, right) => {
      const leftOrder = typeof left.order === 'number' ? left.order : 0;
      const rightOrder = typeof right.order === 'number' ? right.order : 0;
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.id.localeCompare(right.id);
    })
    .map((node) => node.id);
}

function findNodeByOperationId(projectData: ProjectData, nodeId: string | undefined): ProjectNode | undefined {
  if (!nodeId) {
    return undefined;
  }

  const exactNode = projectData.nodes.find((node) => node.id === nodeId);
  if (exactNode) {
    return exactNode;
  }

  const rawOperationNodeId = getNodeId(nodeId);
  return projectData.nodes.find((node) => getNodeId(node.id) === rawOperationNodeId);
}

/**
 * Get all descendant nodes recursively (for delete operations)
 */
function getAllDescendantsRecursive(projectData: ProjectData, nodeId: string): ProjectNode[] {
  const descendants: ProjectNode[] = [];
  const collect = (id: string) => {
    const children = projectData.nodes.filter(n => n.parentId === id);
    children.forEach(child => {
      descendants.push({ ...child });
      collect(child.id);
    });
  };
  collect(nodeId);
  return descendants;
}

/**
 * Enrich operation with before-state data for undo capability
 * Captures the state before an operation is applied for undo/redo
 */
export function enrichOperationWithBeforeState(
  operation: NodeOperation,
  projectData: ProjectData
): NodeOperation {
  const enriched = { ...operation } as NodeOperation & Record<string, unknown>;

  switch (operation.type) {
    case 'update':
      if (operation.nodeId) {
        const node = findNodeByOperationId(projectData, operation.nodeId);
        if (node) {
          enriched.beforeState = { ...node };
        }
      }
      break;

    case 'delete':
    case 'delete-tree':
      if (operation.nodeId) {
        const node = findNodeByOperationId(projectData, operation.nodeId);
        if (node) {
          // Capture the entire tree that will be deleted
          const deletedTree = {
            root: { ...node },
            descendants: operation.type === 'delete-tree' ? getAllDescendantsRecursive(projectData, node.id) : []
          };

          enriched.deletedTree = deletedTree;
          enriched.deletedNode = { ...node }; // Keep for backward compatibility
          enriched.originalIndex = node.order ?? 0;

          // Also capture parent and siblings for proper restoration
          const parentId = node.parentId;
          const siblings = projectData.nodes.filter(n => n.parentId === parentId);
          enriched.deletedNodeContext = {
            parentId,
            siblingCount: siblings.length,
            order: node.order
          };
        }
      }
      break;

    case 'batch-update':
      if (operation.updates) {
        const beforeStates: { nodeId: string; beforeState: ProjectNode }[] = [];
        operation.updates.forEach(update => {
          const node = findNodeByOperationId(projectData, update.nodeId);
          if (node) {
            beforeStates.push({
              nodeId: update.nodeId,
              beforeState: { ...node }
            });
          }
        });
        enriched.beforeStates = beforeStates;
      }
      break;

    case 'batch-delete':
      if (operation.nodeIds) {
        const deletedNodeIds = new Set<string>();
        operation.nodeIds.forEach(nodeId => {
          deletedNodeIds.add(nodeId);
        });

        const deletedNodes: { node: ProjectNode; parentId: string | undefined; originalIndex: number }[] = [];
        deletedNodeIds.forEach(nodeId => {
          const node = projectData.nodes.find(n => n.id === nodeId);
          if (node) {
            deletedNodes.push({
              node: { ...node },
              parentId: node.parentId,
              originalIndex: node.order ?? 0
            });
          }
        });
        enriched.deletedNodes = deletedNodes;
      }
      break;

    case 'move':
      if (operation.nodeId) {
        const node = findNodeByOperationId(projectData, operation.nodeId);
        if (node) {
          const parentId = node.parentId || null;
          enriched.beforeMoveState = {
            parentId: node.parentId,
            order: node.order,
            index: node.order
          };
          enriched.beforeNodeIds = getSiblingNodeIds(projectData, parentId);
        }
      }
      break;

    case 'reorder': {
      const explicitParentId = operation.parentId || null;
      const node = operation.nodeId
        ? projectData.nodes.find(n => n.id === getNodeId(operation.nodeId))
        : undefined;
      const parentId = node ? (node.parentId || null) : explicitParentId;

      if (node) {
        enriched.beforeMoveState = {
          parentId: node.parentId,
          order: node.order,
          index: node.order
        };
      }

      enriched.beforeNodeIds = getSiblingNodeIds(projectData, parentId);
      break;
    }

    case 'reorder-columns':
      if (projectData.kanbanColumns) {
        enriched.beforeColumnOrder = projectData.kanbanColumns.map(col => col.id);
        enriched.beforeAggregateOrder = Object.fromEntries(projectData.kanbanColumns.map(col => [col.id, col.aggregateOrder ?? null]));
      }
      break;

    case 'add-column':
      if (operation.columnData) {
        enriched.columnId = operation.columnId || operation.columnData.id;
      }
      break;

    case 'update-column':
      if (operation.columnId && projectData.kanbanColumns) {
        const column = projectData.kanbanColumns.find(col => normalizeKanbanColumnId(col.id) === normalizeKanbanColumnId(operation.columnId));
        if (column) {
          enriched.beforeColumnData = { ...column };
        }
      }
      break;

    case 'delete-column':
      if (operation.columnId && projectData.kanbanColumns) {
        const column = projectData.kanbanColumns.find(col => normalizeKanbanColumnId(col.id) === normalizeKanbanColumnId(operation.columnId));
        if (column) {
          enriched.deletedColumn = { ...column };
          // Also capture the column order for proper restoration
          enriched.columnOrder = projectData.kanbanColumns.map(col => col.id);
        }
      }
      break;

    case 'group-move':
      if (operation.nodeId && operation.daysDelta !== undefined) {
        // Capture only executable descendants affected by a date-based group move.
        const { movableIds } = classifyGanttGroupMoveDescendants(projectData.nodes, operation.nodeId);
        const beforeStates: { nodeId: string; beforeState: { startDate?: string; endDate?: string; date?: string; dueDate?: string } }[] = [];

        movableIds.forEach(nodeId => {
          const node = projectData.nodes.find(n => n.id === nodeId);
          if (node) {
            beforeStates.push({
              nodeId,
              beforeState: {
                startDate: node.startDate,
                endDate: node.endDate,
                date: node.date,
                ...(node.dueDate !== undefined ? { dueDate: node.dueDate } : {})
              }
            });
          }
        });

        enriched.beforeStates = beforeStates;
        enriched.affectedNodeIds = movableIds;
      }
      break;
  }

  return enriched;
}

/**
 * Generate unique operation ID if not already present
 */
export function ensureOperationId(operation: NodeOperation): NodeOperation {
  const op = operation as NodeOperation & Record<string, unknown>;
  if (!op.id) {
    op.id = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  return op;
}
